import React, { useMemo } from 'react'
import classNames from 'classnames'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import constants from '@/constants'
import currencies from '@/util/currencies'
import { i18n } from '@/locale/i18n'
import { Crevron } from '@/assets/icons'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { getVirtualOutcomesForMonoCustom } from '@/features/market/utils/marketType'
import { isWinnerState, sideMatchesResolutionDisplay } from '@/features/market/outcome-item/utils/outcomeState'
import { ACCORDION_MOTION } from '@/features/market/utils/accordionMotion'
import { rankOutcomesByPrice } from './outcomePricing'
import { useTruncatedLabelTooltip } from './useTruncatedLabelTooltip'
import OutcomeLine from './OutcomeLine'

/**
 * Outcomes the collapsed row shows: the leading two. A binary market is exactly that;
 * a market with more than three leads with its two front-runners and counts the rest on
 * a third line.
 */
const MAX_COMPACT_OUTCOMES = 2
/** A market with this many outcomes or fewer lists them all, with no toggle. */
const MAX_UNCOLLAPSED_OUTCOMES = 3

/**
 * The outcomes of a market row, one to a line: the label, and the probability at the
 * right edge, where the eye can run down the column and compare one market
 * against the next without reading a label. Yes/No, the two sides of a mono custom
 * market and the outcomes of a multi-outcome one are all drawn the same way.
 *
 * Text, not buttons: the line is still the bet control — it selects the outcome and aims
 * the bet pad — but a list of controls read as a list of controls, and this has to read
 * as a table. See OutcomeLine for the line itself.
 *
 * Sports fixtures keep their own compact list (SportEventOutcomesList): their labels come
 * from the match slots rather than the outcome titles.
 */
const CompactOutcomesList = ({
  question,
  ui,
  sortedOutcomes = [],
  currencyId = currencies.OOM.id,
  selectedId,
  globalPosition = null,
  onItemClick,
  onSetActiveTab = () => {},
  isResolved = false,
  /** Whether the row shows every outcome or only the leading pair. */
  expanded = false,
  onToggleExpanded = null,
}) => {
  const marketTypeId = currencyId === currencies.OOM.id ? currencies.OOM.id : question?.canonical_currency
  /* The lines animate in and out on expand, at the timing the row's other accordion runs at. */
  const [linesRef] = useAutoAnimate(ACCORDION_MOTION)
  /* A label the column cut off is read from a hover hint. */
  const [labelHintProps, labelHint] = useTruncatedLabelTooltip()
  const getPercentage = price => Math.round(100 * (price ?? 0))

  const rankedOutcomes = useMemo(
    () => rankOutcomesByPrice(sortedOutcomes, marketTypeId),
    [sortedOutcomes, marketTypeId]
  )

  if (!question || sortedOutcomes.length === 0) return null

  const select = (outcome, position) => () => {
    onSetActiveTab('buy')
    onItemClick(outcome, position)
  }

  /* Every line opens the pad, whatever the market's state — closed, waiting for its
     result, resolved. The pad itself says why no bet can be placed, exactly as it does
     on the market page. */
  const canBet = () => Boolean(onItemClick)

  /* A mono market carries no `resolution` of its own, so `isWinnerState` alone reports no
     winner for either side; the shared helper reads `resolution_display` against the side's
     label, exactly as the card's Yes/No buttons read it. */
  const sideWon = (outcome, position) => isResolved && sideMatchesResolutionDisplay(outcome, question, position)

  /* One description of a line for every market shape; only the source of the label,
     the price and the selected flag differs between them. */
  const buildLines = () => {
    const firstOutcome = sortedOutcomes[0]

    // Mono binary — Yes/No or Up/Down, two positions on one physical outcome.
    if (ui?.displayMode === 'buttons' && firstOutcome) {
      const labels = getPositionLabels(firstOutcome, question)
      const longPrice = firstOutcome.price?.[marketTypeId] ?? 0
      const bettable = canBet(firstOutcome)
      /* The selection is a position, not an outcome: both sides carry the same id, so
         which one is lit comes from the position the card holds. */
      const isOurs = selectedId != null && selectedId === firstOutcome.id
      const sides = [
        { key: 'long', label: labels.longLabel, price: longPrice, position: constants.position.LONG },
        { key: 'short', label: labels.shortLabel, price: 1 - longPrice, position: constants.position.SHORT },
      ]

      return {
        lines: sides.map(side => ({
          key: `${firstOutcome.id}_${side.key}`,
          label: side.label,
          percentage: getPercentage(side.price),
          winner: sideWon(firstOutcome, side.position),
          selected: isOurs && globalPosition === side.position,
          clickable: bettable,
          onClick: select(firstOutcome, side.position),
        })),
        remaining: 0,
      }
    }

    // Mono custom — the same outcome read as two sides, exactly as the card lists it.
    if (ui?.needsVirtualOutcomes && sortedOutcomes.length === 1) {
      const { actualOutcome, virtualOutcomes } = getVirtualOutcomesForMonoCustom(question)
      if (!actualOutcome || virtualOutcomes.length !== 2) return { lines: [], remaining: 0 }

      const bettable = canBet(actualOutcome)

      return {
        lines: virtualOutcomes.map(virtualOutcome => ({
          key: virtualOutcome.virtualId,
          label: virtualOutcome.title,
          percentage: getPercentage(virtualOutcome.price?.[marketTypeId]),
          winner: sideWon(actualOutcome, virtualOutcome.position),
          selected: selectedId != null && selectedId === virtualOutcome.virtualId,
          clickable: bettable,
          onClick: select(actualOutcome, virtualOutcome.position),
        })),
        remaining: 0,
      }
    }

    /* A resolved market has to show its result in the collapsed row. The winner is ranked
       by price like every other outcome, so on a market that settled against the odds it
       sits past the last visible line and the row reads as two greyed lines and no
       trophy; leading with it keeps the result on the first line. */
    const collapsedOutcomes = () => {
      const leading = rankedOutcomes.slice(0, MAX_COMPACT_OUTCOMES)
      if (!isResolved || leading.some(outcome => isWinnerState(outcome, question))) return leading
      const winner = rankedOutcomes.find(outcome => isWinnerState(outcome, question))
      return winner ? [winner, ...leading.slice(0, MAX_COMPACT_OUTCOMES - 1)] : leading
    }

    const collapsible = rankedOutcomes.length > MAX_UNCOLLAPSED_OUTCOMES
    const outcomesToShow = expanded || !collapsible ? rankedOutcomes : collapsedOutcomes()

    return {
      lines: outcomesToShow.map(outcome => ({
        key: String(outcome.id),
        label: outcome.title,
        percentage: getPercentage(outcome.price?.[marketTypeId]),
        winner: isResolved && isWinnerState(outcome, question),
        selected: selectedId != null && selectedId === outcome.id,
        clickable: canBet(outcome),
        onClick: select(
          ui?.placeBet?.outcomeSource === 'first' ? sortedOutcomes[0] : outcome,
          constants.position.LONG
        ),
      })),
      remaining: collapsible ? rankedOutcomes.length - MAX_COMPACT_OUTCOMES : 0,
    }
  }

  const { lines, remaining } = buildLines()
  if (lines.length === 0) return null

  const renderLine = ({ key, label, percentage, winner, selected, clickable, onClick }) => (
    <OutcomeLine
      key={key}
      label={label}
      percentage={percentage}
      winner={winner}
      selected={selected}
      clickable={clickable}
      faded={isResolved && !winner}
      onClick={onClick}
      hintProps={labelHintProps}
    />
  )

  return (
    <div className="flex w-full flex-col gap-1.5" onClick={event => event.stopPropagation()}>
      <div ref={linesRef} className="flex flex-col gap-0.5">
        {lines.map(renderLine)}
      </div>

      {labelHint}

      {remaining > 0 && onToggleExpanded && (
        <button
          type="button"
          onClick={event => {
            event.stopPropagation()
            onToggleExpanded()
          }}
          aria-expanded={expanded}
          className="flex h-4 cursor-pointer items-center gap-1 self-start border-none bg-transparent p-0 text-xs leading-none text-brand-4 hover:text-brand-5">
          <span className="underline-offset-2 hover:underline">
            {expanded ? i18n('markets.view_less_outcomes') : i18n('markets.more_outcomes', { count: remaining })}
          </span>
          {/* Crevron sets its own transform for `direction`, which cannot be
              transitioned; the rotation lives on the wrapper instead. */}
          <span
            className={classNames('inline-flex h-3 w-3 items-center justify-center transition-transform duration-200', {
              'rotate-180': expanded,
            })}>
            <Crevron width="10px" height="10px" fillcolor="currentColor" />
          </span>
        </button>
      )}
    </div>
  )
}

export default CompactOutcomesList
